import { Directive, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';

/**
 * Directive that emits an event when a click occurs outside the host element.
 *
 * @example
 * ```html
 * <div class="dropdown" (clickOutside)="closeDropdown()">
 *   <!-- Dropdown content -->
 * </div>
 * ```
 *
 * The directive tracks both mousedown and mouseup events to determine a true
 * "click outside". This prevents false triggers when:
 * - User starts selecting text inside the element and releases outside
 * - User drags from inside to outside
 *
 * A clickOutside event only fires when both mousedown AND mouseup occur outside
 * the element.
 */
@Directive({
  selector: '[clickOutside]',
  standalone: true,
})
export class ClickOutsideDirective {
  @Output() clickOutside = new EventEmitter<void>();
  private mouseDownInside = false;

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    this.mouseDownInside = this.elementRef.nativeElement.contains(event.target);
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    const mouseUpInside = this.elementRef.nativeElement.contains(event.target);

    // Only emit if both mousedown and mouseup occurred outside
    if (!this.mouseDownInside && !mouseUpInside) {
      this.clickOutside.emit();
    }
  }
}
